import config from 'config';

import { checkboxAnatomy } from '@chakra-ui/anatomy';
import { createMultiStyleConfigHelpers, defineStyle } from '@chakra-ui/react';
import type { StyleFunctionProps } from '@chakra-ui/styled-system';

const { definePartsStyle, defineMultiStyleConfig } =
  createMultiStyleConfigHelpers(checkboxAnatomy.keys);

const baseStyle = definePartsStyle({});

type SchemeStyles = {
  [key: string]: {
    [key: string]: {
      [key: string]: object | string;
    };
  };
};

const {
  colors: {
    palette: {
      theme: { checkbox: styles }
    }
  }
} = config;

const sizes = {
  sm: {
    control: {
      width: '16px',
      height: '16px'
    }
  },
  md: {
    control: {
      width: '20px',
      height: '20px'
    }
  },
  lg: {
    control: {
      width: '24px',
      height: '24px'
    }
  },
  xl: {
    control: {
      width: '30px',
      height: '30px'
    }
  }
};

const SCHEME_STYLES: SchemeStyles = {
  primary: {
    control: {
      borderColor: styles.checkboxPrimaryBorderColor,
      _checked: {
        bg: styles.checkboxPrimaryCheckedBg,
        borderColor: styles.checkboxPrimaryCheckedBorderColor,
        _hover: {
          bg: styles.checkboxPrimaryHoverBg,
          borderColor: `${styles.checkboxPrimaryHoverBorderColor} !important`
        }
      }
    }
  },
  secondary: {
    control: {
      borderColor: styles.checkboxSecondaryBorderColor,
      _checked: {
        bg: styles.checkboxSecondaryCheckedBg,
        borderColor: styles.checkboxSecondaryCheckedBorderColor,
        _hover: {
          bg: styles.checkboxSecondaryHoverBg,
          borderColor: `${styles.checkboxSecondaryHoverBorderColor} !important`
        }
      }
    }
  },
  gray: {
    control: {
      borderColor: styles.checkboxGrayBorderColor,
      _checked: {
        bg: styles.checkboxGrayCheckedBg,
        borderColor: styles.checkboxGrayCheckedBorderColor,
        _hover: {
          bg: styles.checkboxGrayHoverBg,
          borderColor: `${styles.checkboxGrayHoverBorderColor} !important`
        }
      }
    }
  }
};

const solidStyle = defineStyle(
  ({ colorScheme, isInvalid }: StyleFunctionProps) => ({
    ...SCHEME_STYLES[colorScheme],
    control: {
      borderColor: isInvalid
        ? styles.checkboxInvalidBorderColor
        : SCHEME_STYLES[colorScheme].control.borderColor
    }
  })
);

const outlineStyle = defineStyle(
  ({ colorScheme, isInvalid }: StyleFunctionProps) => ({
    ...SCHEME_STYLES[colorScheme],
    control: {
      borderColor: isInvalid
        ? styles.checkboxInvalidBorderColor
        : SCHEME_STYLES[colorScheme].control.borderColor,
      color: styles.checkboxOutlineColor,
      borderRadius: '4px'
    }
  })
);

const circleStyle = defineStyle(
  ({ colorScheme, isInvalid }: StyleFunctionProps) => ({
    ...SCHEME_STYLES[colorScheme],
    control: {
      borderColor: isInvalid
        ? styles.checkboxInvalidBorderColor
        : SCHEME_STYLES[colorScheme].control.borderColor,
      color: styles.checkboxOutlineColor,
      borderRadius: '50%',
      borderWidth: '1px',
      _checked: {
        border: 'none !important'
      },
      '& > div': {
        svg: {
          width: '60%'
        }
      }
    }
  })
);

const Checkbox = defineMultiStyleConfig({
  baseStyle,
  sizes,
  variants: {
    solid: solidStyle,
    outline: outlineStyle,
    circle: circleStyle
  },
  defaultProps: {
    size: 'md',
    variant: 'solid',
    colorScheme: 'primary'
  }
});

export default Checkbox;
